
import React, { useMemo, useState } from "react";
import { ACTION_CARD_COLORS } from "../../utils/actionCardColors";
import { getLocalTimeOrFallback } from "../utils/localDateTime";

const isTruthyFlag = (value) => {
  const normalized = String(value ?? "").trim().toLowerCase();
  return value === true || normalized === "1" || normalized === "true";
};

const getReportingPurposeLabels = (rec, lang) => {
  const labels = [];

  if (isTruthyFlag(rec.has_never_miss)) {
    labels.push(lang === "dk" ? "Near miss" : "Near miss");
  }

  if (isTruthyFlag(rec.has_prevention)) {
    labels.push(lang === "dk" ? "Forebyggelse" : "Prevention");
  }

  if (isTruthyFlag(rec.has_doubt_about_reporting)) {
    labels.push(lang === "dk" ? "Tvivl om rapportering" : "Doubt about Reporting");
  }

  return labels;
};

// Pure helper — safe outside component
const getPurposeLabel = (rec, lang) => {
  const reportingPurposeLabels = getReportingPurposeLabels(rec, lang);
  if (reportingPurposeLabels.length) {
    return reportingPurposeLabels.join(", ");
  }

  const trainingPurpose = Number(rec.training_purpose ?? 0);
  const hasRead = Number(rec.has_read ?? 0);

  if (trainingPurpose === 1 && hasRead === 0) return lang === "dk" ? "Faktisk hændelse" : "Real Incident";
  if (trainingPurpose === 0) return lang === "dk" ? "Træning"  : "Training";
  if (trainingPurpose === 1 && hasRead === 1) return lang === "dk" ? "Læsning"  : "Reading";
  return "-";
};

const hexToRgb = (hex) => {
  const normalized = String(hex || "").replace("#", "");
  const value = parseInt(normalized, 16);

  if (!Number.isFinite(value)) {
    return { r: 242, g: 242, b: 242 };
  }

  return {
    r: (value >> 16) & 255,
    g: (value >> 8) & 255,
    b: value & 255,
  };
};

const getSoftBadgeStyle = (tone) => {
  const { r, g, b } = hexToRgb(tone.fill);

  return {
    background: `rgba(${r}, ${g}, ${b}, 0.16)`,
    border: `1.5px solid ${tone.fill}`,
    color: tone.text,
  };
};

const getPurposeBadgeStyle = (purpose) => {
  const normalized = String(purpose || "").toLowerCase();

  if (normalized.includes("near")) {
    return getSoftBadgeStyle(ACTION_CARD_COLORS.nearMiss);
  }
  if (normalized.includes("prevention") || normalized.includes("forebyggelse")) {
    return getSoftBadgeStyle(ACTION_CARD_COLORS.prevention);
  }
  if (normalized.includes("training") || normalized.includes("træning")) {
    return getSoftBadgeStyle(ACTION_CARD_COLORS.training);
  }
  if (normalized.includes("read") || normalized.includes("læs")) {
    return getSoftBadgeStyle(ACTION_CARD_COLORS.read);
  }
  if (normalized.includes("doubt") || normalized.includes("tvivl")) {
    return getSoftBadgeStyle(ACTION_CARD_COLORS.doubt);
  }
  if (
    normalized.includes("default") ||
    normalized.includes("standard") ||
    normalized.includes("real incident") ||
    normalized.includes("faktisk hændelse")
  ) {
    return getSoftBadgeStyle(ACTION_CARD_COLORS.incident);
  }

  return { background: "#F2F2F2", border: "1px solid #CFCFCF", color: "#555555" };
};

const COLS = [
  { key: "id",         label: "ID" },
  { key: "_date",      label: "Date" },
  { key: "_time",      label: "Time" },
  { key: "nb_set",     label: "Set number" },
  { key: "action_card_number", label: "Action-card no." },
  { key: "_card_name", label: "Actioncard name" },
  { key: "version",    label: "Version" },
  { key: "_purpose",   label: "Purpose" },
  { key: "comment",    label: "Comment" },
  { key: "employee",   label: "Employee" },
];

const COL_LABELS = {
  en: {
    id: "ID",
    _date: "Date",
    _time: "Time",
    nb_set: "Set number",
    action_card_number: "Action-card no.",
    _card_name: "Actioncard name",
    version: "Version",
    _purpose: "Purpose",
    comment: "Comment",
    employee: "Employee",
  },
  dk: {
    id: "ID",
    _date: "Dato",
    _time: "Tid",
    nb_set: "Sæt nr.",
    action_card_number: "Action-card nr.",
    _card_name: "Actioncard navn",
    version: "Version",
    _purpose: "Formål",
    comment: "Kommentar",
    employee: "Medarbejder",
  },
};

const SortIcon = ({ active, dir }) => (
  <span style={{ display: "inline-flex", flexDirection: "column", marginLeft: 4, lineHeight: 1, verticalAlign: "middle" }}>
    <span style={{ opacity: active && dir === "asc"  ? 1 : 0.25, fontSize: 8, lineHeight: 1 }}>▲</span>
    <span style={{ opacity: active && dir === "desc" ? 1 : 0.25, fontSize: 8, lineHeight: 1 }}>▼</span>
  </span>
);

const RawRecordsTable = ({ records = [], actionCards = [], lang = "en" }) => {
  // ── ALL hooks unconditionally at the top ──────────────────────────────────
  const [search,   setSearch]   = useState("");
  const [pageSize, setPageSize] = useState(10);
  const [page,     setPage]     = useState(0);
  const [sortKey,  setSortKey]  = useState("id");
  const [sortDir,  setSortDir]  = useState("desc");

  const actionCardLookup = useMemo(() => {
    const lookup = {};
    actionCards.forEach((card) => {
      lookup[String(card.ac_no)] = {
        en: card.english_short_name,
        dk: card.danish_short_name,
      };
    });
    return lookup;
  }, [actionCards]);

  const localizedCols = useMemo(
    () =>
      COLS.map((col) => ({
        ...col,
        label: COL_LABELS[lang]?.[col.key] ?? COL_LABELS.en[col.key] ?? col.label,
      })),
    [lang]
  );

  const enriched = useMemo(
    () =>
      records.map((r) => {
        const [datePart] = (r.utc_tid_skannet ?? "").split(" ");
        return {
          ...r,
          _date:      datePart  ?? "-",
          _time:      getLocalTimeOrFallback(r.utc_tid_skannet, r.utc_tid_skannet) || "-",
          _card_name: actionCardLookup[String(r.action_card_number)]?.[lang] ?? r.action_card_number ?? "-",
          _purpose:   getPurposeLabel(r, lang),
          _purposeLabels: getReportingPurposeLabels(r, lang),
        };
      }),
    [records, actionCardLookup, lang]
  );

  const searched = useMemo(() => {
    const q = search.toLowerCase().trim();
    if (!q) return enriched;
    return enriched.filter((r) =>
      localizedCols.some((col) => {
        const v = r[col.key];
        return v != null && String(v).toLowerCase().includes(q);
      })
    );
  }, [enriched, localizedCols, search]);

  const sorted = useMemo(
    () =>
      [...searched].sort((a, b) => {
        const av = a[sortKey] ?? "";
        const bv = b[sortKey] ?? "";
        const cmp =
          typeof av === "number"
            ? av - bv
            : String(av).localeCompare(String(bv), undefined, { numeric: true });
        return sortDir === "asc" ? cmp : -cmp;
      }),
    [searched, sortKey, sortDir]
  );

  const totalPages = useMemo(
    () => Math.max(1, Math.ceil(sorted.length / pageSize)),
    [sorted.length, pageSize]
  );

  const safePage = useMemo(
    () => Math.min(page, totalPages - 1),
    [page, totalPages]
  );

  const paginated = useMemo(
    () => sorted.slice(safePage * pageSize, (safePage + 1) * pageSize),
    [sorted, safePage, pageSize]
  );

  const pageButtons = useMemo(() => {
    const start = Math.max(0, safePage - 2);
    const end   = Math.min(totalPages - 1, start + 4);
    return Array.from({ length: end - start + 1 }, (_, i) => start + i);
  }, [safePage, totalPages]);
  // ─────────────────────────────────────────────────────────────────────────

  const handleSort = (key) => {
    if (sortKey === key) {
      setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortKey(key);
      setSortDir("asc");
    }
    setPage(0);
  };

  const startEntry = sorted.length === 0 ? 0 : safePage * pageSize + 1;
  const endEntry   = Math.min((safePage + 1) * pageSize, sorted.length);

  // ── Styles ────────────────────────────────────────────────────────────────
  const thStyle = {
    padding: "10px 12px", textAlign: "center", fontWeight: 700, fontSize: 13,
    color: "#fff", borderBottom: "0", cursor: "pointer",
    whiteSpace: "nowrap", userSelect: "none", background: "#f85b61",
  };
  const tdStyle = {
    padding: "14px 12px", textAlign: "center", fontSize: 13,
    color: "#2f3237", borderBottom: "1px solid #f0f1f4", verticalAlign: "middle", whiteSpace: "nowrap",
  };
  const commentTdStyle = {
    ...tdStyle,
    whiteSpace: "normal",
    wordBreak: "break-word",
    overflowWrap: "anywhere",
    textAlign: "left",
    lineHeight: 1.45,
    minWidth: 340,
    maxWidth: 440,
  };
  const purposeTdStyle = {
    ...tdStyle,
    whiteSpace: "normal",
    overflow: "visible",
  };
  const btnBase     = { padding: "6px 10px", border: "1px solid #e1e4ea", borderRadius: 6, cursor: "pointer", fontSize: 12, background: "#fff", color: "#333" };
  const btnActive   = { ...btnBase, background: "#f85b61", color: "#fff", borderColor: "#f85b61" };
  const btnDisabled = { ...btnBase, opacity: 0.45, cursor: "default" };

  return (
    <div style={{ fontFamily: "sans-serif", marginTop: 8 }}>

      {/* Top bar */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 18, gap: 12, flexWrap: "wrap" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13 }}>
          <span>{lang === "dk" ? "Vis" : "Show"}</span>
          <select
            value={pageSize}
            onChange={(e) => { setPageSize(Number(e.target.value)); setPage(0); }}
            style={{ padding: "3px 6px", border: "1px solid #ccc", borderRadius: 3, fontSize: 13 }}
          >
            {[10, 25, 50, 100].map((n) => <option key={n} value={n}>{n}</option>)}
          </select>
          <span>{lang === "dk" ? "rækker" : "entries"}</span>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13 }}>
          <span>{lang === "dk" ? "Søg:" : "Search:"}</span>
          <input
            value={search}
            onChange={(e) => { setSearch(e.target.value); setPage(0); }}
            style={{ padding: "8px 10px", border: "1px solid #cfd2d8", borderRadius: 6, fontSize: 13, width: 200 }}
          />
        </div>
      </div>

      {/* Table */}
      <div style={{ overflowX: "auto", border: "0", borderRadius: 8 }}>
        <table style={{ width: "100%", borderCollapse: "collapse", minWidth: 1490, tableLayout: "fixed" }}>
          <colgroup>
            <col style={{ width: 70 }} />
            <col style={{ width: 110 }} />
            <col style={{ width: 110 }} />
            <col style={{ width: 90 }} />
            <col style={{ width: 128 }} />
            <col style={{ width: 160 }} />
            <col style={{ width: 90 }} />
            <col style={{ width: 190 }} />
            <col style={{ width: 430 }} />
            <col style={{ width: 140 }} />
          </colgroup>
          <thead>
            <tr>
              {localizedCols.map((col) => (
                <th key={col.key} style={thStyle} onClick={() => handleSort(col.key)}>
                  {col.label}
                  <SortIcon active={sortKey === col.key} dir={sortDir} />
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {paginated.length === 0 ? (
              <tr>
                <td colSpan={localizedCols.length} style={{ ...tdStyle, padding: 28, color: "#999" }}>
                  {lang === "dk" ? "Ingen data fundet" : "No records found"}
                </td>
              </tr>
            ) : (
              paginated.map((row, i) => (
                <tr key={row.id} style={{ background: i % 2 === 0 ? "#fff" : "#f7f7f8" }}>
                  <td style={tdStyle}>{row.id}</td>
                  <td style={tdStyle}>{row._date}</td>
                  <td style={tdStyle}>{row._time}</td>
                  <td style={tdStyle}>{row.nb_set}</td>
                  <td style={tdStyle}>{row.action_card_number || "—"}</td>
                  <td style={tdStyle}>{row._card_name}</td>
                  <td style={tdStyle}>{row.version}</td>
                  <td style={purposeTdStyle}>
                    <span style={{ display: "flex", flexWrap: "wrap", gap: 6, justifyContent: "center" }}>
                      {(row._purposeLabels?.length ? row._purposeLabels : [row._purpose]).map((purpose) => (
                        <span
                          key={purpose}
                          style={{
                            ...getPurposeBadgeStyle(purpose),
                            borderRadius: 999,
                            boxSizing: "border-box",
                            display: "inline-flex",
                            alignItems: "center",
                            justifyContent: "center",
                            maxWidth: "100%",
                            minHeight: 34,
                            fontSize: 12.5,
                            fontWeight: 700,
                            lineHeight: 1.25,
                            padding: "7px 14px",
                            textAlign: "center",
                            whiteSpace: "normal",
                            overflowWrap: "anywhere",
                          }}
                        >
                          {purpose}
                        </span>
                      ))}
                    </span>
                  </td>
                  <td style={commentTdStyle}>{row.comment || "—"}</td>
                  <td style={tdStyle}>{row.employee}</td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: 10, fontSize: 13 }}>
        <span style={{ color: "#555" }}>
          {sorted.length === 0
            ? (lang === "dk" ? "Viser 0 rækker" : "Showing 0 entries")
            : lang === "dk"
              ? `Viser ${startEntry} til ${endEntry} af ${sorted.length} rækker`
              : `Showing ${startEntry} to ${endEntry} of ${sorted.length} entries`}
        </span>
        <div style={{ display: "flex", gap: 4 }}>
          <button
            style={safePage === 0 ? btnDisabled : btnBase}
            disabled={safePage === 0}
            onClick={() => setPage((p) => p - 1)}
          >
            {lang === "dk" ? "Forrige" : "Previous"}
          </button>
          {pageButtons.map((p) => (
            <button key={p} style={p === safePage ? btnActive : btnBase} onClick={() => setPage(p)}>
              {p + 1}
            </button>
          ))}
          <button
            style={safePage >= totalPages - 1 ? btnDisabled : btnBase}
            disabled={safePage >= totalPages - 1}
            onClick={() => setPage((p) => p + 1)}
          >
            {lang === "dk" ? "Næste" : "Next"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default RawRecordsTable;
